import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, KeyRound, Power, Building2, Files, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge, EmptyState, Field, Spinner } from "@/components/common";
import { api, formatErr } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const BLANK = { name: "", email: "", username: "", employee_code: "", mobile: "", status: "active", password: "" };

const UserDialog = ({ role, open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...BLANK, ...initial, password: "" } : BLANK); }, [open, initial]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, role };
      initial ? await api.put(`/users/${initial.id}`, body) : await api.post("/users", body);
      toast.success(initial ? "Account updated" : "Account created");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const fields = [["name", "Full name", true], ["employee_code", "Employee code (auto if blank)"], ["email", "Email", true], ["username", "Username", true], ["mobile", "Mobile"]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-xl" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit account" : `New ${role} account`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {fields.map(([k, l, req]) => (
            <Field key={k} label={l} required={req}><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`user-form-${k}`} /></Field>
          ))}
          <Field label="Status">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="user-form-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
            </Select>
          </Field>
          {!initial && <div className="col-span-2"><Field label="Initial password" required hint="Minimum 8 characters. Share securely with the user."><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-form-password" /></Field></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="user-form-submit">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResetDialog = ({ target, onOpenChange }) => {
  const [pw, setPw] = useState("");
  useEffect(() => setPw(""), [target]);
  const save = async () => {
    try { await api.post(`/users/${target.id}/reset-password`, { new_password: pw }); toast.success("Password reset"); onOpenChange(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Reset password</DialogTitle><DialogDescription>{target?.name} · {target?.email}</DialogDescription></DialogHeader>
        <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8 chars)" data-testid="reset-password-input" />
        <DialogFooter><Button disabled={pw.length < 8} onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="reset-password-submit">Reset</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AccessDialog = ({ target, onOpenChange, onSaved }) => {
  const [clients, setClients] = useState([]);
  const [all, setAll] = useState(false);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    if (!target) return;
    api.get("/clients").then(({ data }) => setClients(data));
    setAll(!!target.auditor_all_clients);
    setSel(target.client_ids || []);
  }, [target]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    try { await api.put(`/users/${target.id}/auditor-access`, { all_clients: all, client_ids: sel }); toast.success("Auditor access updated"); onSaved(); onOpenChange(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="auditor-access-dialog">
        <DialogHeader><DialogTitle>Client access · {target?.name}</DialogTitle><DialogDescription>Auditors get read-only access to bill values and attachments for authorised clients.</DialogDescription></DialogHeader>
        <label className="flex items-center gap-3 p-3 border border-slate-200 rounded"><Switch checked={all} onCheckedChange={setAll} data-testid="auditor-access-all" /><span className="text-sm">Access to all clients</span></label>
        {!all && (
          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
            {clients.map((c, i) => (
              <label key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                <Checkbox checked={sel.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`auditor-access-client-${i}`} />
                <span className="flex-1">{c.company_name}</span><span className="num text-[11px] text-slate-400">{c.code}</span>
              </label>
            ))}
            {!clients.length && <div className="p-4 text-sm text-slate-400">No clients yet.</div>}
          </div>
        )}
        <DialogFooter><Button onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="auditor-access-submit">Save access</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Users({ role }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [reset, setReset] = useState(null);
  const [access, setAccess] = useState(null);
  const load = () => api.get("/users", { params: { role } }).then(({ data }) => setRows(data));
  useEffect(() => { setRows(null); load(); }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (u) => {
    try { await api.post(`/users/${u.id}/status`, { status: u.status === "active" ? "inactive" : "active" }); toast.success("Status updated"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const isStaff = role === "staff";
  const shown = (rows || []).filter((u) => !q || `${u.name} ${u.email} ${u.username} ${u.employee_code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader eyebrow="Management" title={isStaff ? "Staff management" : "Auditor management"}
        subtitle={isStaff ? "Data entry operators. Assign clients from the Clients page." : "Read-only reviewers. Grant access to all or selected clients."}
        actions={<Button onClick={() => setEdit({})} className="bg-emerald-800 hover:bg-emerald-900" data-testid="add-user-button"><Plus className="w-4 h-4 mr-1.5" />New {role}</Button>} />
      <div className="panel overflow-hidden fade-up">
        <div className="px-4 py-3 border-b border-slate-200"><div className="relative max-w-md"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" placeholder="Search name, email, code…" data-testid="user-search-input" /></div></div>
        <div className="overflow-x-auto">
          {!rows ? <Spinner /> : !shown.length ? <EmptyState title={`No ${role} accounts yet`} /> : (
            <table className="w-full" data-testid="users-table">
              <thead><tr>{["Code", "Name", "Username", "Mobile", isStaff ? "Clients" : "Access", "Bills", "Last login", "Status", ""].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
              <tbody>
                {shown.map((u, i) => (
                  <tr key={u.id} className="row-hover" data-testid={`user-row-${i}`}>
                    <td className="td num text-xs text-slate-500">{u.employee_code}</td>
                    <td className="td"><div className="font-medium">{u.name}</div><div className="text-[11px] text-slate-400">{u.email}</div></td>
                    <td className="td num text-xs">{u.username}</td>
                    <td className="td text-sm">{u.mobile || "—"}</td>
                    <td className="td text-sm">{isStaff ? <span className="num">{u.client_count}</span> : u.auditor_all_clients ? "All clients" : <span className="num">{u.client_ids?.length || 0} clients</span>}</td>
                    <td className="td num text-sm">{u.bill_count}</td>
                    <td className="td num text-[11px] text-slate-500">{fmtDateTime(u.last_login_at)}</td>
                    <td className="td"><StatusBadge status={u.status} /></td>
                    <td className="td text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEdit(u)} title="Edit" data-testid={`user-edit-${i}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setReset(u)} title="Reset password" data-testid={`user-reset-${i}`}><KeyRound className="w-4 h-4" /></Button>
                      {!isStaff && <Button size="sm" variant="ghost" onClick={() => setAccess(u)} title="Client access" data-testid={`user-access-${i}`}><Building2 className="w-4 h-4" /></Button>}
                      {isStaff && <Button size="sm" variant="ghost" onClick={() => nav(`/bills?staff_id=${u.id}`)} title="Entry history" data-testid={`user-bills-${i}`}><Files className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => toggle(u)} title={u.status === "active" ? "Deactivate" : "Activate"} className={u.status === "active" ? "text-red-600" : "text-emerald-700"} data-testid={`user-toggle-${i}`}><Power className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <UserDialog role={role} open={!!edit} onOpenChange={(o) => !o && setEdit(null)} initial={edit?.id ? edit : null} onSaved={load} />
      <ResetDialog target={reset} onOpenChange={() => setReset(null)} />
      <AccessDialog target={access} onOpenChange={() => setAccess(null)} onSaved={load} />
    </div>
  );
}
